import { FinanceTransaction, FinancialGoal, BudgetConfig } from '../types';
import { getStoredUser, apiGet, apiPost, apiPut, apiDelete } from './api';

let inMemoryTransactions: FinanceTransaction[] = [];
let inMemoryGoals: FinancialGoal[] = [];
let inMemoryBudgetConfig: BudgetConfig = {
  monthlyIncomeAfterTax: 0,
  currency: 'ریال',
  expenseLimitRatio: 60,
  savingLimitRatio: 40,
  categoryCaps: {}
};
let inMemoryUserCards: string[] = ['Swish', 'Debit Card', 'Credit Card', 'Cash'];

// Subscribers
const subscribers = new Set<(data: { transactions: FinanceTransaction[]; goals: FinancialGoal[]; budgetConfig: BudgetConfig; userCards: string[] }) => void>();

function notifySubscribers() {
  const payload = {
    transactions: [...inMemoryTransactions],
    goals: [...inMemoryGoals],
    budgetConfig: { ...inMemoryBudgetConfig },
    userCards: [...inMemoryUserCards]
  };
  subscribers.forEach(cb => {
    try {
      cb(payload);
    } catch (err) {
      console.error('Error notifying finance subscriber:', err);
    }
  });
}

// Fetch all finance data from canonical server
async function fetchFinanceDataFromCloud(): Promise<void> {
  const user = getStoredUser();
  if (!user) return;

  try {
    // 1. Fetch budget config
    const budgetRes = await apiGet<any>('/api/finance/budget');
    if (budgetRes.success && budgetRes.data) {
      inMemoryBudgetConfig = {
        monthlyIncomeAfterTax: budgetRes.data.monthlyIncomeAfterTax || 0,
        currency: budgetRes.data.currency || 'SEK',
        expenseLimitRatio: budgetRes.data.expenseLimitRatio || 70,
        savingLimitRatio: budgetRes.data.savingLimitRatio || 20,
        categoryCaps: budgetRes.data.categoryCaps || {}
      };
    }

    // 2. Fetch accounts (to map to userCards)
    const accountsRes = await apiGet<any[]>('/api/finance/accounts');
    if (accountsRes.success && Array.isArray(accountsRes.data)) {
      if (accountsRes.data.length > 0) {
        inMemoryUserCards = accountsRes.data.map(acc => acc.name || acc.account_name);
      } else {
        // Seed default accounts on server
        const defaults = ['Swish', 'Debit Card', 'Credit Card', 'Cash'];
        for (const def of defaults) {
          await apiPost('/api/finance/accounts', { account_name: def, account_type: 'card' });
        }
        inMemoryUserCards = defaults;
      }
    }

    // 3. Fetch goals
    const goalsRes = await apiGet<any[]>('/api/finance/goals');
    if (goalsRes.success && Array.isArray(goalsRes.data)) {
      inMemoryGoals = goalsRes.data.map(g => ({
        id: g.id,
        title: g.title,
        targetAmount: g.targetAmount || g.target_amount || 0,
        currentSavings: g.currentSavings || g.current_amount || 0,
        currency: g.currency || inMemoryBudgetConfig.currency,
        targetDate: g.targetDate || g.target_date,
        category: g.category || 'other',
        notes: g.notes || '',
        createdAt: g.created_at || new Date().toISOString()
      }));
    }

    // 4. Fetch transactions
    const txsRes = await apiGet<any[]>('/api/finance/transactions');
    if (txsRes.success && Array.isArray(txsRes.data)) {
      inMemoryTransactions = txsRes.data.map(t => ({
        id: t.id,
        type: t.type || t.transaction_type || 'expense',
        title: t.title,
        amount: t.amount || 0,
        currency: t.currency || inMemoryBudgetConfig.currency,
        paymentMethod: t.paymentMethod || t.payment_method || 'card',
        cardAccount: t.cardAccount || t.card_account || '',
        category: t.category || 'other',
        date: t.date || t.transaction_date || new Date().toISOString().split('T')[0],
        notes: t.notes || '',
        createdAt: t.created_at || new Date().toISOString()
      }));
    }

    notifySubscribers();
  } catch (err) {
    console.warn('Error fetching finance data from cloud:', err);
  }
}

// Real-time Subscription - pure Cloudflare D1 data flow
export function subscribeToFinanceData(
  onUpdate: (data: { transactions: FinanceTransaction[]; goals: FinancialGoal[]; budgetConfig: BudgetConfig; userCards: string[] }) => void
): () => void {
  subscribers.add(onUpdate);

  // Send current state to subscriber
  onUpdate({
    transactions: [...inMemoryTransactions],
    goals: [...inMemoryGoals],
    budgetConfig: { ...inMemoryBudgetConfig },
    userCards: [...inMemoryUserCards]
  });

  fetchFinanceDataFromCloud();

  return () => {
    subscribers.delete(onUpdate);
  };
}

export async function updateBudgetConfig(config: BudgetConfig): Promise<void> {
  const previousConfig = { ...inMemoryBudgetConfig };
  inMemoryBudgetConfig = { ...config };
  notifySubscribers();

  const user = getStoredUser();
  if (user) {
    try {
      const res = await apiPut('/api/finance/budget', {
        monthlyIncomeAfterTax: config.monthlyIncomeAfterTax,
        currency: config.currency,
        expenseLimitRatio: config.expenseLimitRatio,
        savingLimitRatio: config.savingLimitRatio,
        categoryCaps: config.categoryCaps
      });
      if (!res.success) {
        inMemoryBudgetConfig = previousConfig;
        notifySubscribers();
        throw new Error(res.error || 'خطا در ثبت تنظیمات بودجه');
      }
    } catch (err) {
      inMemoryBudgetConfig = previousConfig;
      notifySubscribers();
      throw err;
    }
  }
}

// --- TRANSACTIONS ---
export async function addTransaction(transaction: Omit<FinanceTransaction, 'id' | 'createdAt'>): Promise<FinanceTransaction> {
  const tempId = `tx_${Date.now()}`;
  const newTx: FinanceTransaction = {
    ...transaction,
    id: tempId,
    createdAt: new Date().toISOString()
  };

  inMemoryTransactions = [newTx, ...inMemoryTransactions];
  notifySubscribers();

  const user = getStoredUser();
  if (user) {
    try {
      const res = await apiPost('/api/finance/transactions', {
        title: transaction.title,
        type: transaction.type,
        amount: transaction.amount,
        currency: transaction.currency,
        category: transaction.category,
        payment_method: transaction.paymentMethod,
        cardAccount: transaction.cardAccount,
        date: transaction.date,
        notes: transaction.notes
      });
      if (res.success && res.data) {
        newTx.id = res.data.id;
        newTx.createdAt = res.data.created_at || newTx.createdAt;
        inMemoryTransactions = inMemoryTransactions.map(t => t.id === tempId ? { ...newTx } : t);
        notifySubscribers();
      } else {
        inMemoryTransactions = inMemoryTransactions.filter(t => t.id !== tempId);
        notifySubscribers();
        throw new Error(res.error || 'خطا در ثبت تراکنش');
      }
    } catch (err) {
      inMemoryTransactions = inMemoryTransactions.filter(t => t.id !== tempId);
      notifySubscribers();
      throw err;
    }
  }

  return newTx;
}

export async function addBatchTransactions(transactionsList: Omit<FinanceTransaction, 'id' | 'createdAt'>[]): Promise<FinanceTransaction[]> {
  const createdTxs: FinanceTransaction[] = [];
  for (const tx of transactionsList) {
    const res = await addTransaction(tx);
    createdTxs.push(res);
  }
  return createdTxs;
}

export async function updateTransaction(transaction: FinanceTransaction): Promise<void> {
  const previousTx = inMemoryTransactions.find(t => t.id === transaction.id);
  const updatedTx = { ...transaction, updatedAt: new Date().toISOString() };
  inMemoryTransactions = inMemoryTransactions.map(t => t.id === transaction.id ? updatedTx : t);
  notifySubscribers();

  const user = getStoredUser();
  if (user) {
    try {
      const res = await apiPut(`/api/finance/transactions/${transaction.id}`, {
        title: transaction.title,
        type: transaction.type,
        amount: transaction.amount,
        currency: transaction.currency,
        category: transaction.category,
        payment_method: transaction.paymentMethod,
        cardAccount: transaction.cardAccount,
        date: transaction.date,
        notes: transaction.notes
      });
      if (!res.success) {
        if (previousTx) {
          inMemoryTransactions = inMemoryTransactions.map(t => t.id === transaction.id ? previousTx : t);
          notifySubscribers();
        }
        throw new Error(res.error || 'خطا در به روزرسانی تراکنش');
      }
    } catch (err) {
      if (previousTx) {
        inMemoryTransactions = inMemoryTransactions.map(t => t.id === transaction.id ? previousTx : t);
        notifySubscribers();
      }
      throw err;
    }
  }
}

export async function deleteTransaction(transactionId: string): Promise<void> {
  const previousTx = inMemoryTransactions.find(t => t.id === transactionId);
  inMemoryTransactions = inMemoryTransactions.filter(t => t.id !== transactionId);
  notifySubscribers();

  const user = getStoredUser();
  if (user) {
    try {
      const res = await apiDelete(`/api/finance/transactions/${transactionId}`);
      if (!res.success) {
        if (previousTx) {
          inMemoryTransactions = [previousTx, ...inMemoryTransactions];
          notifySubscribers();
        }
        throw new Error(res.error || 'خطا در حذف تراکنش');
      }
    } catch (err) {
      if (previousTx) {
        inMemoryTransactions = [previousTx, ...inMemoryTransactions];
        notifySubscribers();
      }
      throw err;
    }
  }
}

export async function deleteTransactionsByIds(idsToDelete: string[]): Promise<void> {
  for (const id of idsToDelete) {
    await deleteTransaction(id);
  }
}

export async function deleteTransactionsByMonth(yearMonth: string): Promise<void> {
  const toDelete = inMemoryTransactions.filter(t => t.date && t.date.startsWith(yearMonth));
  for (const t of toDelete) {
    await deleteTransaction(t.id);
  }
}

export async function deleteTransactionsByYear(year: string): Promise<void> {
  const toDelete = inMemoryTransactions.filter(t => t.date && t.date.startsWith(year));
  for (const t of toDelete) {
    await deleteTransaction(t.id);
  }
}

export async function deleteAllTransactions(): Promise<void> {
  const toDelete = [...inMemoryTransactions];
  for (const t of toDelete) {
    await deleteTransaction(t.id);
  }
}

// --- GOALS ---
export async function addFinancialGoal(goal: Omit<FinancialGoal, 'id' | 'createdAt'>): Promise<FinancialGoal> {
  const tempId = `goal_${Date.now()}`;
  const newGoal: FinancialGoal = {
    ...goal,
    id: tempId,
    createdAt: new Date().toISOString()
  };

  inMemoryGoals = [newGoal, ...inMemoryGoals];
  notifySubscribers();

  const user = getStoredUser();
  if (user) {
    try {
      const res = await apiPost('/api/finance/goals', {
        title: goal.title,
        target_amount: goal.targetAmount,
        current_amount: goal.currentSavings,
        target_date: goal.targetDate,
        category: goal.category,
        color: 'emerald'
      });
      if (res.success && res.data) {
        newGoal.id = res.data.id;
        newGoal.createdAt = res.data.created_at || newGoal.createdAt;
        inMemoryGoals = inMemoryGoals.map(g => g.id === tempId ? { ...newGoal } : g);
        notifySubscribers();
      } else {
        inMemoryGoals = inMemoryGoals.filter(g => g.id !== tempId);
        notifySubscribers();
        throw new Error(res.error || 'خطا در ایجاد هدف مالی');
      }
    } catch (err) {
      inMemoryGoals = inMemoryGoals.filter(g => g.id !== tempId);
      notifySubscribers();
      throw err;
    }
  }

  return newGoal;
}

export async function updateFinancialGoal(goal: FinancialGoal): Promise<void> {
  const previousGoal = inMemoryGoals.find(g => g.id === goal.id);
  const updatedGoal = { ...goal, updatedAt: new Date().toISOString() };
  inMemoryGoals = inMemoryGoals.map(g => g.id === goal.id ? updatedGoal : g);
  notifySubscribers();

  const user = getStoredUser();
  if (user) {
    try {
      const res = await apiPut(`/api/finance/goals/${goal.id}`, {
        title: goal.title,
        target_amount: goal.targetAmount,
        current_amount: goal.currentSavings,
        target_date: goal.targetDate,
        category: goal.category,
        color: 'emerald'
      });
      if (!res.success) {
        if (previousGoal) {
          inMemoryGoals = inMemoryGoals.map(g => g.id === goal.id ? previousGoal : g);
          notifySubscribers();
        }
        throw new Error(res.error || 'خطا در ویرایش هدف مالی');
      }
    } catch (err) {
      if (previousGoal) {
        inMemoryGoals = inMemoryGoals.map(g => g.id === goal.id ? previousGoal : g);
        notifySubscribers();
      }
      throw err;
    }
  }
}

export async function deleteFinancialGoal(goalId: string): Promise<void> {
  const previousGoal = inMemoryGoals.find(g => g.id === goalId);
  inMemoryGoals = inMemoryGoals.filter(g => g.id !== goalId);
  notifySubscribers();

  const user = getStoredUser();
  if (user) {
    try {
      const res = await apiDelete(`/api/finance/goals/${goalId}`);
      if (!res.success) {
        if (previousGoal) {
          inMemoryGoals = [previousGoal, ...inMemoryGoals];
          notifySubscribers();
        }
        throw new Error(res.error || 'خطا در حذف هدف مالی');
      }
    } catch (err) {
      if (previousGoal) {
        inMemoryGoals = [previousGoal, ...inMemoryGoals];
        notifySubscribers();
      }
      throw err;
    }
  }
}

export async function depositToGoal(goalId: string, additionalAmount: number): Promise<void> {
  const goal = inMemoryGoals.find(g => g.id === goalId);
  if (goal) {
    goal.currentSavings = Math.max(0, goal.currentSavings + additionalAmount);
    await updateFinancialGoal(goal);
  }
}

export async function saveAdvisorChatsToCloudD1(messages: Array<{ id: string; role: 'user' | 'model'; text: string; time: string }>): Promise<void> {
  const user = getStoredUser();
  if (!user) return;

  try {
    // Fetch currently saved messages to avoid duplicates or re-save everything cleanly by posting any new messages
    const currentRes = await apiGet<any[]>('/api/finance/advisor');
    const existingIds = new Set((currentRes.success && Array.isArray(currentRes.data)) ? currentRes.data.map(m => m.id) : []);

    for (const msg of messages) {
      if (!existingIds.has(msg.id)) {
        await apiPost('/api/finance/advisor', {
          sender: msg.role === 'user' ? 'user' : 'ai',
          message_text: msg.text
        });
      }
    }
  } catch (err) {
    console.warn('Failed to save advisor messages to server:', err);
  }
}

export async function getAdvisorChatsFromCloudD1(): Promise<Array<{ id: string; role: 'user' | 'model'; text: string; time: string }> | null> {
  const user = getStoredUser();
  if (!user) return null;

  try {
    const res = await apiGet<any[]>('/api/finance/advisor');
    if (res.success && Array.isArray(res.data)) {
      return res.data.map(m => ({
        id: m.id,
        role: (m.sender === 'user' ? 'user' : 'model') as 'user' | 'model',
        text: m.message_text || m.text || '',
        time: m.created_at || new Date().toISOString()
      }));
    }
  } catch (err) {
    console.warn('Failed to fetch advisor messages from server:', err);
  }
  return null;
}

export async function updateUserCards(newCards: string[]): Promise<void> {
  inMemoryUserCards = [...newCards];
  notifySubscribers();

  const user = getStoredUser();
  if (user) {
    try {
      // Keep accounts on server in sync with userCards list
      const currentRes = await apiGet<any[]>('/api/finance/accounts');
      const currentNames = new Set((currentRes.success && Array.isArray(currentRes.data)) ? currentRes.data.map(acc => (acc.name || acc.account_name || '')) : []);

      for (const card of newCards) {
        if (!currentNames.has(card)) {
          await apiPost('/api/finance/accounts', { account_name: card, account_type: 'card' });
        }
      }

      for (const acc of (currentRes.success && Array.isArray(currentRes.data) ? currentRes.data : [])) {
        const name = acc.name || acc.account_name || '';
        if (!newCards.includes(name)) {
          await apiDelete(`/api/finance/accounts/${acc.id}`);
        }
      }
    } catch (err) {
      console.warn('Failed to sync user accounts/cards with server:', err);
    }
  }
}
